import * as React from "react";
import { useEffect, useState, useCallback } from "react";
import { graphql } from "gatsby";
import PageContainer from "../pageContainer.js";
import styled from "styled-components";
import { GatsbyImage } from "gatsby-plugin-image";
import { SectionTitle } from "../typography.js";
import useEmblaCarousel from "embla-carousel-react";
import { when } from "../../styles/theme.js";
import { useLocalization } from "gatsby-theme-i18n";
import pageBackground from "../../images/page_background_red.jpg";

export const fragment = graphql`
  fragment ProductsContent on ContentfulPageSectionProducts {
    title
    descriptions {
      childMarkdownRemark {
        html
      }
    }
    products {
      productName
      productDescriptions {
        childMarkdownRemark {
          html
        }
      }
      productImage {
        description
        gatsbyImageData(
          layout: CONSTRAINED
          width: 400
          height: 400
          placeholder: NONE
        )
      }
    }
    supermarket1 {
      description
      gatsbyImageData(
        layout: CONSTRAINED
        width: 185
        height: 40
        placeholder: NONE
      )
    }
    supermarket2 {
      description
      gatsbyImageData(
        layout: CONSTRAINED
        width: 185
        height: 40
        placeholder: NONE
      )
    }
    supermarket3 {
      description
      gatsbyImageData(
        layout: CONSTRAINED
        width: 185
        height: 40
        placeholder: NONE
      )
    }
    supermarket4 {
      description
      gatsbyImageData(
        layout: CONSTRAINED
        width: 185
        height: 40
        placeholder: NONE
      )
    }
    internal {
      type
    }
  }
`;
const Wrapper = styled.section`
  background: ${(props) => props.theme.colors.red};
  padding-top: 5rem;
  padding-bottom: 10rem;
  color: ${(props) => props.theme.colors.white};
  position: relative;
`;

const ContainerBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  //z-index: -1;
  background-image: url(${pageBackground});
  background-repeat: repeat;
  background-position: center;
`;

const Container = styled(PageContainer)`
  position: relative;
  align-items: flex-start;
  ${when("md")} {
    display: flex;
    column-gap: 5.4rem;
  }
`;

const SectionInfo = styled.div`
  ${when("lg")} {
    display: flex;
    flex: 1;
  }
`;

const Title = styled(SectionTitle)`
  text-align: center;
  color: ${(props) => props.theme.colors.white};

  ${when("md")} {
    text-align: left;
    max-width: 23rem;
  }
`;

const Description = styled.div`
  margin: 2rem auto 0;
  max-width: 50rem;

  ${when("md")} {
    margin-top: 1rem;
    flex: 1;
  }

  ${when("lg")} {
    margin-left: 8rem;
  }
`;

const Supermarkets = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: 2rem;
  margin-left: -1rem;
  margin-right: -1rem;

  ${when("md")} {
    margin-top: -1rem;
    width: 35.4rem;
    flex: 0 0 auto;
  }
`;

const Supermarket = styled.div`
  width: 50%;
  padding: 1rem;
  text-align: ${(props) => (props.isAlignedLeft ? "left" : "right")};
`;

const SupermarketLogo = styled(GatsbyImage)`
  border-radius: 1rem;
  overflow: hidden;
`;

const CarouselWrapper = styled.div`
  margin-top: 5rem;
  overflow: hidden;
  position: relative;
  max-width: 100vw;

  ${when("md")} {
    margin-top: 6rem;
  }
`;

const ProductContainer = styled.div`
  display: flex;

  ${when("lg")} {
    justify-content: space-between;
  }
`;

const Product = styled.div`
  position: relative;
  flex: 0 0 60%;
  margin-left: 1rem;
  margin-right: 1rem;

  ${when("md")} {
    flex: 0 0 33.333%;
    margin-left: 2rem;
    margin-right: 2rem;
  }
`;

const ProductInner = styled.div`
  display: flex;
  height: 100%;
  transform: ${(props) => (props.isActive ? "none" : "scale(0.8)")};
  transition: transform 0.25s ease-in-out;

  ${when("xxl")} {
    max-width: 50rem;
    margin: 0 auto;
  }
`;

const ProductContent = styled.div`
  flex: 1;
`;

const ProductImage = styled(GatsbyImage)`
  transition: opacity 0.25s ease-in-out;
  opacity: ${(props) => (props.$isActive ? "1" : "0.5")};
`;

const ProductDescription = styled.div`
  margin-top: 4rem;
  transition: opacity 0.25s ease-in-out;
  opacity: ${(props) => (props.isActive ? "1" : "0")};
`;

const ProductName = styled.div`
  text-align: center;
  writing-mode: vertical-rl;
  font-size: 3.4rem;
  letter-spacing: ${(props) => (props.isEn ? "0" : "1rem")};
  transition: opacity 0.25s ease-in-out;
  opacity: ${(props) => (props.isActive ? "1" : "0")};
  text-orientation: ${(props) => (props.isEn ? "sideways" : "upright")};
  ${(props) =>
    props.isEn &&
    `
      line-height: 1;
      text-align: left;
      height: 30rem;
      margin-left: 2rem;
      margin-top: 4rem;
  `}

  ${when("xxl")} {
    font-size: 4rem;
  }
`;

const Controls = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
`;

const LeftControl = styled.div`
  position: absolute;
  top: 50%;
  left: 20%;
  padding: 1rem;
  transform: translate(-4.5rem, -3rem);
  cursor: pointer;
  pointer-events: auto;

  ${when("md")} {
    left: 33.333%;
    transform: translate(-8.5rem, -3rem);
  }
`;

const Left = styled.div`
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 1.25rem 1.25rem 1.25rem 0;
  border-color: transparent #fff transparent transparent;
`;

const Right = styled(Left)`
  transform: scaleX(-1);
`;

const RightControl = styled(LeftControl)`
  left: unset;
  right: 20%;
  transform: translate(4.5rem, -3rem);

  ${when("md")} {
    right: 33.333%;
    //   transform: translate(0, -3rem);
    transform: translate(8.5rem, -3rem);
  }
`;

const Products = ({ data }) => {
  const { locale } = useLocalization();

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: "center",
    loop: true,
  });

  const [selectedIndex, setSelectedIndex] = useState(0);

  useEffect(() => {
    if (emblaApi) {
      emblaApi.on("select", (eventName) => {
        setSelectedIndex(emblaApi.selectedScrollSnap());
      });
    }
  }, [emblaApi]);

  const scrollPrev = useCallback(() => {
    if (emblaApi) emblaApi.scrollPrev();
  }, [emblaApi]);

  const scrollNext = useCallback(() => {
    if (emblaApi) emblaApi.scrollNext();
  }, [emblaApi]);

  return (
    <Wrapper id="packing-products">
      <ContainerBackground />
      <Container>
        <SectionInfo>
          <Title>{data.title}</Title>
          <Description
            dangerouslySetInnerHTML={{
              __html: data.descriptions?.childMarkdownRemark.html,
            }}
          />
        </SectionInfo>
        <Supermarkets>
          <Supermarket>
            <SupermarketLogo
              alt={data.supermarket1?.description}
              image={data.supermarket1?.gatsbyImageData}
            />
          </Supermarket>
          <Supermarket isAlignedLeft={true}>
            <SupermarketLogo
              alt={data.supermarket2?.description}
              image={data.supermarket2?.gatsbyImageData}
            />
          </Supermarket>
          <Supermarket>
            <SupermarketLogo
              alt={data.supermarket3?.description}
              image={data.supermarket3?.gatsbyImageData}
            />
          </Supermarket>
          <Supermarket isAlignedLeft={true}>
            <SupermarketLogo
              alt={data.supermarket4?.description}
              image={data.supermarket4?.gatsbyImageData}
            />
          </Supermarket>
        </Supermarkets>
      </Container>
      <CarouselWrapper ref={emblaRef}>
        <ProductContainer>
          {data.products.map((product, index) => {
            const isActive = selectedIndex === index;
            return (
              <Product key={index}>
                <ProductInner isActive={isActive}>
                  <ProductContent>
                    {product.productImage && (
                      <ProductImage
                        alt={product.productImage.description}
                        image={product.productImage.gatsbyImageData}
                        $isActive={isActive}
                        objectFit="contain"
                      />
                    )}
                    <ProductDescription
                      dangerouslySetInnerHTML={{
                        __html:
                          product.productDescriptions?.childMarkdownRemark.html,
                      }}
                      isActive={isActive}
                    />
                  </ProductContent>
                  <ProductName isActive={isActive} isEn={locale === "en"}>
                    {product.productName}
                  </ProductName>
                </ProductInner>
              </Product>
            );
          })}
        </ProductContainer>
        <Controls>
          <LeftControl onClick={scrollPrev}>
            <Left />
          </LeftControl>
          <RightControl onClick={scrollNext}>
            <Right />
          </RightControl>
        </Controls>
      </CarouselWrapper>
    </Wrapper>
  );
};

export const productsName = "ContentfulPageSectionProducts";

export default Products;
